import { PILOT_STANDARD } from "./assumptions";

/** Addy keeps LLM cost rollups 35 days; the backend reads at most 30. */
export const COST_WINDOW_MAX_DAYS = 30;
const STANDARD_PILOT_DAYS = PILOT_STANDARD.weeks * 7;

/** Fields that describe one pilot, cleared when another pilot is picked. */
export const PILOT_SPECIFIC_KEYS = [
  "developers",
  "loanOfficers",
  "loansPerOfficer",
  "costPerRun",
  "runsPerLoan",
];

export const PLAN_PILOT_ID = "plan";

const roundTo = (value, digits) => Math.round(value * 10 ** digits) / 10 ** digits;

/**
 * Days of real cost to read for a pilot: since it started, capped at what the
 * backend keeps. A pilot with no known start gets the standard three weeks.
 */
export function costWindowDays(pilot) {
  const days = Number(pilot?.daysInPilot);
  if (pilot?.daysInPilot === null || pilot?.daysInPilot === undefined || !Number.isFinite(days)) {
    return STANDARD_PILOT_DAYS;
  }

  return Math.min(Math.max(Math.ceil(days), 1), COST_WINDOW_MAX_DAYS);
}

/**
 * Real cost per loan across several clients, weighted by their loans.
 *
 * @param {Array<{ data: object|null }>} entries results of the costs endpoint
 * @returns {{ costPerLoanUsd: number, pilots: number, loans: number, isPartial: boolean }|null}
 */
export function averageCostPerLoan(entries) {
  const withLoans = entries
    .map((entry) => entry.data)
    .filter((data) => data && data.loansProcessed > 0);
  if (!withLoans.length) {
    return null;
  }
  const loans = withLoans.reduce((sum, data) => sum + data.loansProcessed, 0);
  const totalUsd = withLoans.reduce((sum, data) => sum + data.totalUsd, 0);

  return {
    costPerLoanUsd: totalUsd / loans,
    pilots: withLoans.length,
    loans,
    isPartial: withLoans.some((data) => data.extraction === null || data.isTruncated),
  };
}

/**
 * Calculator values read from HubSpot and Addy's database for a pilot, or
 * for a new pilot plan when `pilot` is null, with where each one came from.
 * Anything without a real source is left out, so the team standard shows.
 *
 * @returns {{ values: object, sources: object }}
 */
export function buildPilotCostPrefill({ pilot, costs, team, average }) {
  const values = {};
  const sources = {};
  const set = (key, value, source) => {
    values[key] = value;
    sources[key] = source;
  };

  if (pilot) {
    set("developers", pilot.fde ? 1 : 0, "hubspot");
    if (team?.accepted > 0) {
      set("loanOfficers", team.accepted, "db");
    }
  }
  if (pilot && costs?.costPerLoanUsd !== null && costs?.costPerLoanUsd !== undefined) {
    set("costPerRun", roundTo(costs.costPerLoanUsd, 2), "db");
    set("runsPerLoan", 1, "db");
  } else if (average) {
    set("costPerRun", roundTo(average.costPerLoanUsd, 2), "average");
  }

  return { values, sources };
}

/**
 * What the calculator shows: the team standard, overridden by real data,
 * overridden by what the user typed.
 *
 * @returns {{ values: object, sources: object }}
 */
export function resolveCostInputs({ prefill, edits }) {
  const sources = Object.fromEntries(Object.keys(PILOT_STANDARD).map((key) => [key, "standard"]));
  Object.assign(sources, prefill.sources);
  Object.keys(edits).forEach((key) => {
    sources[key] = "edited";
  });

  return {
    values: { ...PILOT_STANDARD, costPerRun: "", ...prefill.values, ...edits },
    sources,
  };
}

/**
 * The full pilot at the pace so far, or null once the pilot is past its
 * planned length or has not processed anything.
 */
export function projectAtPace({ costs, daysElapsed, plannedDays }) {
  if (!costs || !(daysElapsed > 0) || daysElapsed >= plannedDays || !costs.loansProcessed) {
    return null;
  }
  const factor = plannedDays / daysElapsed;

  return {
    loans: Math.round(costs.loansProcessed * factor),
    totalUsd: costs.totalUsd * factor,
  };
}
